import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { planRowDate } from '../utils/planRowDate';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, ChevronRight, Flame, Beef, Wheat, Droplet, Calendar, Plus, RefreshCw, Trash2, AlertTriangle, Sparkles, X, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import RemainingMacrosHeader from '../components/RemainingMacrosHeader';
import { PairSwapDialog } from '../components/PairSwapDialog';
import { guidedPlanningAPI, mealPlanAPI, mealAPI, planningAPI } from '../utils/api';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { trackMealAddToDay, trackMealRemoveFromDay } from '@/lib/posthog';
import { nutritionStateLabel, savedDayStatusLabel, PLANNING_STATUS } from '../utils/planningStatus';
import { getMacroBarColor } from '../utils/macroStatus';
import { getTodayString, formatFullDateWithOrdinal } from '../utils/dateUtils';
import { safeReturnPath, finishFlow, buildUrl, startFlowState, planPathForDates } from '../utils/planLocation';
import { recordTasteSignal, recordSwapTasteSignals, TASTE_ACTIONS } from '../utils/tasteSignals';
import { nextGuidedStep, guidedSnackStep, snackAvailable, pendingSnackMealIds } from '../utils/guidedNextStep';
import { GUIDED_REQUEST_TIMEOUT_MS, isAbortError, newRequestController, readWithOneRetry } from '../utils/guidedRequests';
import { reportAutoFixTerminal } from '../utils/autoFixDiagnostics';
import { getMealConflicts, conflictText } from '../utils/mealConflicts';

// Placeholder SVG generator for missing images
function getPlaceholderSvg(mealType) {
  var colors = {
    Breakfast: '#4A90A4',
    Lunch: '#6B8E23',
    Dinner: '#8B4513',
    Snack: '#DAA520',
  };
  var color = colors[mealType] || '#333333';
  var letter = mealType ? mealType.substring(0, 1) : 'M';
  return (
    'data:image/svg+xml,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="' +
        color +
        '" width="80" height="80"/><text fill="white" font-family="Arial" font-size="32" font-weight="bold" x="50%" y="55%" text-anchor="middle">' +
        letter +
        '</text></svg>',
    )
  );
}

// Meal selection card component (for choosing new meals) - matches Home page format
function MealSelectionCard({ meal, mealType, index, adding, onAddMeal, onViewMeal, targetsReady, alreadyAdded }) {
  var isAdding = adding === meal.meal_id;
  var imageUrl = meal.image_url || getPlaceholderSvg(mealType);
  var statusLabel = targetsReady ? nutritionStateLabel(meal.nutrition_state) : null;
  var cookTime = Number(meal.cook_time_minutes) > 0 ? Number(meal.cook_time_minutes) + ' min' : null;
  // Saved-dislike warning ONLY (Build 39 QA #6). Uses the shared Browse/Swap
  // conflict detection on the preference facts this endpoint already returns,
  // and surfaces just the `dislike` entry — cookware, cook time, same-day and
  // every other conflict are intentionally not shown here. The meal stays
  // selectable; it is de-emphasized like a Browse card with a dislike.
  var dislikeConflict = alreadyAdded ? null : getMealConflicts(meal).find(function (c) { return c.key === 'dislike'; });

  return (
    <motion.div
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: dislikeConflict ? 0.7 : 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className={'bg-card border rounded-xl overflow-hidden transition-all ' +
        (dislikeConflict ? 'border-orange-500/50 bg-orange-500/5' : 'border-border/40 hover:border-accent/50')}
      data-testid={'meal-' + meal.meal_id}
      data-dislike={dislikeConflict ? 'true' : 'false'}
    >
      <div className="flex gap-3 p-3">
        <div
          onClick={function () {
            onViewMeal(meal);
          }}
          className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-secondary/50 cursor-pointer"
        >
          <img
            src={imageUrl}
            alt={meal.name}
            className="w-full h-full object-cover"
            onError={function (e) {
              e.target.src = getPlaceholderSvg(mealType);
            }}
          />
        </div>
        <div
          onClick={function () {
            onViewMeal(meal);
          }}
          className="flex-1 min-w-0 cursor-pointer"
        >
          <p className="font-heading font-semibold text-sm truncate" title={meal.name}>
            {meal.name}
          </p>
          <div className="flex items-center gap-2 text-[11px] mt-1">
            <span>
              <b>{meal.macros.calories}</b> cal
            </span>
            <span>
              <b>{meal.macros.protein}g</b> P
            </span>
            <span>
              <b>{meal.macros.carbs}g</b> C
            </span>
            <span>
              <b>{meal.macros.fat}g</b> F
            </span>
          </div>
          {dislikeConflict && (
            <div
              className="mt-1.5 w-fit max-w-full px-2 py-0.5 rounded-lg text-[11px] font-medium flex items-center gap-1 bg-amber-500 text-white"
              title={conflictText(dislikeConflict)}
              data-testid={'dislike-warning-' + meal.meal_id}
            >
              <AlertTriangle size={11} />
              <span className="truncate">{dislikeConflict.label}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-[11px] mt-2" data-testid="candidate-metadata">
            <span className="text-accent" data-testid="candidate-status">
              {statusLabel || ''}
            </span>
            {alreadyAdded ? (
              <span className="text-muted-foreground font-medium" data-testid={'already-added-' + meal.meal_id}>
                Already added
              </span>
            ) : cookTime ? (
              <span className="text-muted-foreground" data-testid="candidate-cook-time">
                {cookTime}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center">
          <Button
            size="sm"
            onClick={function (e) {
              e.stopPropagation();
              if (!isAdding && !alreadyAdded) onAddMeal(meal);
            }}
            disabled={isAdding || !!alreadyAdded}
            className="rounded-full h-9 w-9 p-0 bg-accent hover:bg-accent/90"
            data-testid={'add-meal-btn-' + meal.meal_id}
          >
            {isAdding ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check size={16} />}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// Planned meal card (for viewing/editing already planned meals) - updated to match Home format
function PlannedMealCard({ plan, mealDetails, onSwap, onRemove, onViewMeal, removing }) {
  var isRemoving = removing === plan.plan_id;
  var isLogged = plan.status === 'completed';
  var [confirmRemove, setConfirmRemove] = useState(false);
  var meal = mealDetails || {};
  var imageUrl = meal.image_url || getPlaceholderSvg(plan.meal_type);

  // A saved row shows its OWN authoritative macros (GET /meal-plans/daily
  // `macros`: scale, edits, logged / custom values, leftover portion) -- the
  // same numbers the saved-day total counts. Catalog details supply the image
  // only; their recipe-level numbers never stand in for a persisted row.
  var rowMacros = plan.macros || {};
  var calories = Math.round(rowMacros.calories || 0);
  var protein = Math.round(rowMacros.protein || 0);
  var carbs = Math.round(rowMacros.carbs || 0);
  var fat = Math.round(rowMacros.fat || 0);

  function handleImageError(e) {
    e.target.src = getPlaceholderSvg(plan.meal_type);
  }

  function handleCardClick() {
    if (onViewMeal) {
      onViewMeal(plan);
    }
  }

  return (
    <div className="bg-card border border-border/40 rounded-xl overflow-hidden">
      <div className="flex gap-3 p-3">
        {/* Left: Square thumbnail - clickable */}
        <div onClick={handleCardClick} className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-secondary/50 cursor-pointer">
          <img src={imageUrl} alt={plan.meal_name} className="w-full h-full object-cover" onError={handleImageError} />
        </div>

        {/* Middle: Stacked content - clickable */}
        <div onClick={handleCardClick} className="flex-1 min-w-0 flex flex-col justify-center cursor-pointer">
          {/* Meal type + logged marker */}
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
            {plan.meal_type}
            {isLogged ? <span className="ml-1.5 text-accent font-semibold" data-testid="logged-badge">• Logged</span> : null}
          </span>

          {/* Meal name */}
          <p className="font-heading font-semibold text-sm truncate" title={plan.meal_name}>
            {plan.meal_name}
          </p>

          {/* Full macro row */}
          <div className="flex items-center gap-2 text-[11px] mt-0.5">
            <span className="text-muted-foreground">
              <span className="font-mono font-medium text-foreground">{calories}</span> cal
            </span>
            <span className="text-muted-foreground">
              <span className="font-mono font-medium text-foreground">{protein}g</span> P
            </span>
            <span className="text-muted-foreground">
              <span className="font-mono font-medium text-foreground">{carbs}g</span> C
            </span>
            <span className="text-muted-foreground">
              <span className="font-mono font-medium text-foreground">{fat}g</span> F
            </span>
          </div>
        </div>

        {/* Right: Action buttons */}
        <div className="flex items-center gap-1">
          {!isLogged ? (
            <button
              onClick={function (e) {
                e.stopPropagation();
                onSwap(plan);
              }}
              className="p-2 rounded-full text-muted-foreground hover:bg-secondary transition-colors"
              title="Swap meal"
            >
              <RefreshCw size={14} />
            </button>
          ) : null}
          <button
            onClick={function (e) {
              e.stopPropagation();
              if (isLogged) setConfirmRemove(true);
              else onRemove(plan.plan_id);
            }}
            disabled={isRemoving}
            className="p-2 rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
            title="Remove meal"
            data-testid={'remove-planned-' + plan.plan_id}
          >
            {isRemoving ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span> : <Trash2 size={14} />}
          </button>
        </div>
      </div>
      {confirmRemove ? (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="remove-logged-confirm" onClick={function (e) { e.stopPropagation(); }}>
          <div className="bg-background rounded-xl p-5 max-w-sm w-full">
            <h3 className="font-semibold">Remove this logged meal?</h3>
            <p className="text-sm text-muted-foreground my-3">This will delete the logged meal from this day.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={function (e) { e.stopPropagation(); setConfirmRemove(false); }} data-testid="remove-logged-cancel">Cancel</Button>
              <Button
                onClick={function (e) {
                  e.stopPropagation();
                  setConfirmRemove(false);
                  onRemove(plan.plan_id);
                }}
                data-testid="remove-logged-confirm-btn"
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// What a canonical reload resolves to when it no longer owns the screen: a
// newer reload started after it, or the date it was asked for is no longer on
// screen. Nothing from it was applied, so a caller claims neither success nor
// failure for the day now shown.
var STALE_RELOAD = { stale: true, complete: false };

// Expanded Step-by-Step search thresholds (frontend presentation only).
var GUIDED_SEARCH_MIN_CHARS = 2;
var GUIDED_SEARCH_LOCAL_MIN = 5;
var GUIDED_SEARCH_DEBOUNCE_MS = 350;
var GUIDED_SEARCH_MAX_RESULTS = 20;

// The instant local filter over the recommendations already on screen.
export function filterMealsLocally(meals, searchQuery) {
  var query = String(searchQuery || '').trim().toLowerCase();
  if (!query) return meals || [];
  return (meals || []).filter(function (m) {
    var nameMatch = m.name && m.name.toLowerCase().includes(query);
    var ingredientMatch =
      m.ingredients &&
      m.ingredients.some(function (ing) {
        var ingName = typeof ing === 'string' ? ing : ing.name || '';
        return ingName.toLowerCase().includes(query);
      });
    var tagMatch =
      m.tags &&
      Object.keys(m.tags).some(function (tag) {
        return m.tags[tag] && tag.toLowerCase().includes(query);
      });
    return nameMatch || ingredientMatch || tagMatch;
  });
}

// Step-by-Step loading diagnostics (weekly spinner investigation). Every
// canonical / candidate / search request lifecycle event and every change of
// the rendered branch is appended to a bounded in-memory ring buffer
// (window.__fxGuidedDiag, last 300 events) so a staging repro can be captured
// from the console; it is also printed when localStorage fx_guided_diag === '1'.
// Diagnostics never change behavior.
var GUIDED_DIAG_LIMIT = 300;
export function guidedDiag(event, data) {
  try {
    var entry = Object.assign({ t: Date.now(), event: event }, data || {});
    if (typeof window !== 'undefined') {
      var buf = window.__fxGuidedDiag || (window.__fxGuidedDiag = []);
      buf.push(entry);
      if (buf.length > GUIDED_DIAG_LIMIT) buf.splice(0, buf.length - GUIDED_DIAG_LIMIT);
      if (window.localStorage && window.localStorage.getItem('fx_guided_diag') === '1') {
        // eslint-disable-next-line no-console
        console.debug('[GuidedDiag]', entry);
      }
    }
  } catch (e) {
    // diagnostics must never affect the screen
  }
}

function GuidedPlanning() {
  var navigate = useNavigate();
  var location = useLocation();
  var [searchParams, setSearchParams] = useSearchParams();

  // Parse dates from URL
  var datesParam = searchParams.get('dates') || searchParams.get('date') || getTodayString();
  var allDates = datesParam.split(',').filter(function (d) {
    return d;
  });

  // Single slot fill mode (from Home page "Add Meal" flow)
  var fillSlotParam = searchParams.get('fillSlot');
  var isSingleSlotMode = !!fillSlotParam;

  // Mode param (auto vs manual for regenerate)
  var modeParam = searchParams.get('mode');

  // Cookware preferences from URL (passed from PlanFoodLauncher)
  var passedCookware = searchParams.get('cookware');
  var userCookware = passedCookware ? passedCookware.split(',') : [];
  var passedMaxCookTime = searchParams.get('maxCookTime');
  var userMaxCookTime = passedMaxCookTime ? parseInt(passedMaxCookTime, 10) : null;

  // Where Step-by-Step returns when the user leaves or finishes: exactly where
  // it was opened from (Plan's Daily/Weekly location, or Home).
  var returnTarget = safeReturnPath(searchParams.get('return'), '/plan');
  // The current day and meal type also live in the URL (day=, type=), so a
  // return from Swap / Browse All / a meal's detail, or a refresh, resumes this
  // step of a multi-day plan instead of restarting on Day 1.
  var dayParam = searchParams.get('day');
  var typeParam = searchParams.get('type');
  var restoredType = ['Breakfast', 'Lunch', 'Dinner', 'Snack'].indexOf(typeParam) !== -1 ? typeParam : null;
  var restoredTypeRef = useRef(restoredType);

  // Multi-day state
  var [currentDateIndex, setCurrentDateIndex] = useState(Math.max(0, allDates.indexOf(dayParam)));
  var [currentMealType, setCurrentMealType] = useState(fillSlotParam || restoredType || null);
  var [status, setStatus] = useState(null);
  var [meals, setMeals] = useState([]);
  var [plannedMeals, setPlannedMeals] = useState([]); // Meals already planned for current day
  var [mealDetails, setMealDetails] = useState({}); // Full meal details for planned meals
  // Canonical day loads and candidate loads each own their loading flag, so
  // one kind finishing can never end the other's spinner.
  var [canonicalLoading, setCanonicalLoading] = useState(true);
  var [candidateLoading, setCandidateLoading] = useState(false);
  var [adding, setAdding] = useState(null);
  var [removing, setRemoving] = useState(null);
  var [remaining, setRemaining] = useState(null);
  var [target, setTarget] = useState(null);
  var [candidateTargetsReady, setCandidateTargetsReady] = useState(false);
  var [canonicalError, setCanonicalError] = useState(null);
  var [candidateError, setCandidateError] = useState(null);
  var [showAllComplete, setShowAllComplete] = useState(false);
  var [completedDays, setCompletedDays] = useState({});

  // Review & Fix mode state
  var [reviewResult, setReviewResult] = useState(null);
  var [applyingFix, setApplyingFix] = useState(false);
  var [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  // Pair-safe Replace Both for a linked cook-once / eat-twice row on this date.
  var [pairSwapContext, setPairSwapContext] = useState(null);

  // Search state for filtering meals
  var [searchQuery, setSearchQuery] = useState('');
  // Expanded search over the slot's full eligible catalog (same backend
  // endpoint, same eligibility / ranking), used only when the instant local
  // filter leaves too few matches. Results belong to one date|slot|query.
  var [expandedResults, setExpandedResults] = useState([]);
  var [expandedSearching, setExpandedSearching] = useState(false);
  var searchRequestRef = useRef(0);
  var searchCacheRef = useRef({}); // this visit only: date|slot|query -> meals

  var isMultiDay = allDates.length > 1;
  var currentDate = allDates[currentDateIndex];
  var mealSequence = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
  var loading = canonicalLoading || candidateLoading;
  // A Snack step lists addable Snack candidates only while the backend's
  // shared Snack rule says another Snack is available (can_add_snacks). A saved
  // Snack stays viewable (Selected Meals) either way. Non-Snack steps are never
  // gated here. While this date's FIRST canonical load is still in flight (a
  // Snack step restored from the URL, or entered before the status landed)
  // the Snack candidates may load in PARALLEL instead of waiting a full serial
  // round trip; they stay hidden behind the spinner and unselectable, and the
  // moment the authoritative status lands without an available Snack the
  // candidate request is aborted.
  var candidatesAllowed = currentMealType !== 'Snack' || (status ? snackAvailable(status) : canonicalLoading);
  // Dates whose Snack step the user explicitly finished (✓ Finish) this visit:
  // a later refresh keeps their Day Overview instead of re-entering Snack.
  var snackFinishedRef = useRef({});

  // Latest-request identity (the same pattern as Browse). Every canonical day
  // load and every candidate load takes the next number, and only the newest
  // of its kind may change this screen: an older response -- success, failure
  // or finally -- is a no-op, so a slow Day 1 / Breakfast answer can never
  // overwrite Day 2 / Lunch.
  var canonicalRequestRef = useRef(0);
  var candidateRequestRef = useRef(0);
  // The live HTTP request behind each latest id. A retired request is ABORTED
  // (not merely ignored when it lands), so superseded / abandoned work stops
  // holding sockets and server capacity.
  var canonicalAbortRef = useRef(null);
  var candidateAbortRef = useRef(null);
  var searchAbortRef = useRef(null);
  // The date the `status` in state belongs to. A refresh of the SAME date keeps
  // the last known-good status on screen state (so the Snack step's candidate
  // request is not killed and restarted by a transient null); another date's
  // status is never shown.
  var statusDateRef = useRef(null);
  // True from a write this screen made on the saved day (add / remove / swap /
  // Auto-Fix) until a canonical reload of that day completes: the status in
  // hand is then no longer the saved day.
  var savedDayChangedRef = useRef(false);
  // Linked-context reads also own navigation/dialog/toast state. Only the
  // latest request for the date still on screen may act on its response.
  var linkedContextRequestRef = useRef(0);
  // The date|slot the candidate cards on screen were ranked for (null while
  // they load). A card is only ever saved into that slot.
  var candidateSlotRef = useRef(null);
  // The date on screen, for async work that began on another date. Whenever it
  // changes -- day controls, a day indicator or a new dates= URL -- what belonged
  // to the previous date is dropped before paint: a step restored from the URL
  // (type=) belongs to the day restored with it, and the Review & Fix panel,
  // its result and the Accept As Is confirmation belong to the day they were
  // produced for. The new date's own canonical load is left untouched.
  var currentDateRef = useRef(currentDate);
  // Per-date version of the completed/check state: a canonical load for a date
  // supersedes any mount-time reconciliation still in flight for it.
  var completionVersionRef = useRef({});
  // One-shot: a canonical refresh triggered by an explicit Day Overview action
  // (Remove) keeps the Day Overview on screen instead of advancing to the
  // status's next_meal_type. Cleared at the end of that refresh. It is also
  // seeded true on mount when returning from an Overview-origin Swap/Browse
  // (overview=1 marker in the URL), since the in-memory flag is lost across the
  // component remount that a navigation to /meals causes.
  var overviewPreserveRef = useRef(searchParams.get('overview') === '1');
  useLayoutEffect(
    function () {
      if (currentDateRef.current !== currentDate) {
        // Permanently retire linked-context reads from the screen state being
        // left. Returning to that date must not revive an older request.
        linkedContextRequestRef.current += 1;
        restoredTypeRef.current = null;
        setReviewResult(null);
        setShowAcceptConfirm(false);
        setPairSwapContext(null);
      }
      currentDateRef.current = currentDate;
    },
    [currentDate],
  );

  function formatDate(dateStr) {
    var d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }

  function formatShortDate(dateStr) {
    var d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
  }

  // This exact step — every current param kept (dates, return, cookware,
  // max cook time) with the current day + meal type — as the return target
  // for Swap / Browse All / a meal's detail opened from here. When opened from
  // the Day Overview (no active meal step), an explicit one-shot overview=1
  // marker is added so the return remounts back on the Overview and the first
  // canonical reload does not pull the user into next_meal_type.
  function getCurrentReturnUrl() {
    var params = new URLSearchParams(searchParams);
    params.set('day', currentDate);
    if (currentMealType) {
      params.set('type', currentMealType);
      params.delete('overview');
    } else {
      params.delete('type');
      params.set('overview', '1');
    }
    return '/plan/guided?' + params.toString();
  }

  // View meal details with proper return navigation.
  // Accepts an optional plan object so the meal view can hydrate any
  // saved edits for a logged/planned instance.
  function viewMeal(mealId, plan) {
    var returnUrl = getCurrentReturnUrl();
    var params = new URLSearchParams();
    params.set('return', returnUrl);
    params.set('date', planRowDate(plan, currentDate));
    if (currentMealType) params.set('type', currentMealType);
    if (!plan) {
      params.set('guided', 'true');
      params.set('candidate', '1');
    }
    if (plan) {
      if (plan.plan_id) params.set('planId', plan.plan_id);
      if (plan.status) params.set('status', plan.status);
      if (plan.scale_factor && plan.scale_factor !== 1.0) {
        params.set('scaleFactor', plan.scale_factor);
        if (plan.scale_label) params.set('scaleLabel', plan.scale_label);
      }
    }
    navigate('/meal-view/' + mealId + '?' + params.toString(), {
      state: startFlowState(),
    });
  }

  // View meal from selection list
  function handleViewSelectionMeal(meal) {
    viewMeal(meal.meal_id);
  }

  // View meal from planned list
  function handleViewPlannedMeal(plan) {
    viewMeal(plan.meal_id, plan);
  }

  // Fetch meal details for planned meals. They belong to the canonical request
  // that loaded these rows and are dropped if a newer one has started since.
  function fetchMealDetails(plans, requestId) {
    if (!plans || plans.length === 0) return;

    var uniqueMealIds = [];
    var seen = {};
    for (var i = 0; i < plans.length; i++) {
      var mealId = plans[i].meal_id;
      if (!seen[mealId]) {
        seen[mealId] = true;
        uniqueMealIds.push(mealId);
      }
    }

    var fetchPromises = uniqueMealIds.map(function (id) {
      return mealAPI.getMeal(id).catch(function () {
        return null;
      });
    });

    Promise.all(fetchPromises).then(function (results) {
      if (requestId !== canonicalRequestRef.current) return;
      var detailsMap = {};
      for (var i = 0; i < results.length; i++) {
        if (results[i] && results[i].data) {
          detailsMap[results[i].data.meal_id] = results[i].data;
        }
      }
      setMealDetails(detailsMap);
    });
  }

  // Fetch status and planned meals for current day
  var fetchStatus = useCallback(
    function () {
      // Guard: prevent fetch without valid date
      if (!currentDate) {
        return;
      }
      // Only the date on screen is ever loaded onto it: a reload asked for by
      // work that began on another date (an Auto Fix or chosen Swap finishing
      // after the user moved on) must not replace the current date's day.
      if (currentDate !== currentDateRef.current) {
        return Promise.resolve(STALE_RELOAD);
      }
      var requestId = canonicalRequestRef.current + 1;
      canonicalRequestRef.current = requestId;
      var requestDate = currentDate;
      // A newer canonical load retires (aborts) the previous one.
      if (canonicalAbortRef.current) canonicalAbortRef.current.abort();
      var controller = newRequestController();
      canonicalAbortRef.current = controller;
      guidedDiag('canonical:start', { date: requestDate, id: requestId });
      // Any mount-time completion reconciliation already in flight for this
      // date is superseded by this canonical load.
      completionVersionRef.current[requestDate] = (completionVersionRef.current[requestDate] || 0) + 1;

      setCanonicalLoading(true);
      setCanonicalError(null);
      // A status belongs to exactly one canonical date. Never display the
      // previous date while either of this date's reads is in flight. A reload
      // of the SAME date keeps its last status in state (the screen shows the
      // spinner meanwhile): clearing it would flip the Snack step's
      // eligibility and needlessly kill / restart its candidate request.
      if (statusDateRef.current !== requestDate) {
        statusDateRef.current = null;
        setStatus(null);
        setPlannedMeals([]);
        setMealDetails({});
      }

      // Each read has a finite boundary and is aborted when retired; as
      // idempotent GETs they are retried once on a timeout / network failure.
      // Promise.allSettled so a failure on one call does not blank out the
      // other. Neither endpoint is Pro-gated; this is purely fault-tolerance.
      function timed(name, read) {
        var started = Date.now();
        return read.then(function (res) {
          guidedDiag('canonical:' + name, { date: requestDate, id: requestId, ms: Date.now() - started, ok: true });
          return res;
        }, function (err) {
          guidedDiag('canonical:' + name, { date: requestDate, id: requestId, ms: Date.now() - started, ok: false,
            aborted: isAbortError(err), code: err && err.code });
          throw err;
        });
      }
      return Promise.allSettled([
        timed('status', readWithOneRetry(function (opts) { return guidedPlanningAPI.getStatus(requestDate, opts); },
          controller.signal, GUIDED_REQUEST_TIMEOUT_MS)),
        timed('plan', readWithOneRetry(function (opts) { return mealPlanAPI.getDailyPlan(requestDate, opts); },
          controller.signal, GUIDED_REQUEST_TIMEOUT_MS)),
      ])
        .then(function (results) {
          // A newer canonical request (another date, a retry or a refresh)
          // owns the screen: this older response, loaded or failed, is a no-op.
          var stale = requestId !== canonicalRequestRef.current;
          guidedDiag('canonical:settle', { date: requestDate, id: requestId, stale: stale,
            status: results[0].status, plans: results[1].status });
          if (stale) return STALE_RELOAD;

          var statusRes = results[0];
          var planRes = results[1];

          var statusData = statusRes.status === 'fulfilled' && statusRes.value ? statusRes.value.data : null;
          var planData = planRes.status === 'fulfilled' && planRes.value ? planRes.value.data : null;

          if (statusRes.status === 'rejected') {
            console.error('[GuidedPlanning] Status fetch failed:', statusRes.reason);
          }
          if (planRes.status === 'rejected') {
            console.error('[GuidedPlanning] Daily plan fetch failed:', planRes.reason);
          }

          var statusOk = statusRes.status === 'fulfilled' && !!statusData;
          var plansOk = planRes.status === 'fulfilled' && Array.isArray(planData);

          if (statusOk) {
            setStatus(statusData);
            statusDateRef.current = requestDate;
          } else {
            // Fail closed: no status the saved day no longer matches.
            setStatus(null);
            statusDateRef.current = null;
          }
          // Always set plannedMeals — to whatever loaded, or [] — so stale
          // meals from a prior date are not displayed if planData failed.
          if (plansOk) setPlannedMeals(planData);
          else setPlannedMeals([]);
          if (statusOk && plansOk) savedDayChangedRef.current = false;

          // Fetch meal details for planned meals
          if (plansOk && planData.length > 0) {
            fetchMealDetails(planData, requestId);
          }

          if (!statusOk || !plansOk) {
            setCanonicalError('We could not reload the saved day. Retry before continuing.');
          }

          // Reconcile THIS date's completed/check state from the authoritative
          // Guided status: complete === planning_complete AND the saved day is
          // Within Range. Set both true and false on every reload so a day that
          // becomes incomplete (or drifts off-range) is un-checked, and a
          // remount re-derives it instead of trusting transient session state.
          var reconciledDate = currentDate;
          var isReconciledComplete = !!(statusData && statusData.planning_complete && savedDayStatusLabel(statusData) === PLANNING_STATUS.WITHIN);
          setCompletedDays(function (prev) {
            if (prev[reconciledDate] === isReconciledComplete) return prev;
            var next = Object.assign({}, prev);
            next[reconciledDate] = isReconciledComplete;
            return next;
          });

          // Determine what to show - only update if changed. Only the Guided
          // flow decision (guided_snack_step) sends the user into a Snack
          // step; a merely AVAILABLE Snack is an optional Overview choice.
          if (statusData && statusData.planning_complete && !guidedSnackStep(statusData)) {
            // Day is complete - but we still show the editable view
            setCurrentMealType(function (prev) {
              return prev === null ? prev : null;
            });
          } else if (statusData && statusData.next_meal_type) {
            // An explicit action from Day Overview (e.g. Remove) preserves the
            // Overview for its own refresh: do not let next_meal_type pull the
            // user into a step. The one-shot flag is cleared below. A date whose
            // Snack step the user already finished (✓ Finish) is not pulled back
            // into Snack by a later refresh either.
            var finishedSnack = statusData.next_meal_type === 'Snack' && !!snackFinishedRef.current[currentDate];
            if (finishedSnack) {
              setCurrentMealType(function (prev) {
                return prev === null ? prev : null;
              });
            } else if (!overviewPreserveRef.current) {
              setCurrentMealType(function (prev) {
                return prev === statusData.next_meal_type ? prev : statusData.next_meal_type;
              });
            }
          } else if (statusData) {
            // No next meal but day might have some meals
            setCurrentMealType(function (prev) {
              return prev === null ? prev : null;
            });
          } else {
            // Fail closed. Without canonical status there is no truthful next
            // primary, Snack decision, completion state, or saved-day total.
            setCurrentMealType(null);
          }

          // Returning to this step (type= in the URL): keep that meal type on the
          // first load while its slot is still unplanned; once it has been planned
          // the next-slot logic above continues the flow as usual. A Snack step
          // is kept only while this refreshed status's Guided flow decision
          // still asks for one: the backend, never a stale URL, decides it.
          var keepType = restoredTypeRef.current;
          restoredTypeRef.current = null;
          if (keepType && statusData && (statusData.planned_meal_types || []).indexOf(keepType) === -1 &&
              (keepType !== 'Snack' || guidedSnackStep(statusData))) {
            setCurrentMealType(keepType);
          }
          // Consume the one-shot Overview-preservation flag for this refresh.
          overviewPreserveRef.current = false;
          return { statusOk: statusOk, plansOk: plansOk, statusData: statusData,
            planData: planData, complete: statusOk && plansOk };
        })
        .finally(function () {
          var current = requestId === canonicalRequestRef.current;
          guidedDiag('canonical:finally', { date: requestDate, id: requestId, clearsLoading: current });
          if (canonicalAbortRef.current === controller) canonicalAbortRef.current = null;
          if (current) setCanonicalLoading(false);
        });
    },
    [currentDate],
  ); // Only depends on currentDate

  var fetchMeals = useCallback(
    function () {
      if (!currentMealType || !currentDate) return;

      var requestId = candidateRequestRef.current + 1;
      candidateRequestRef.current = requestId;
      var slot = currentDate + '|' + currentMealType;
      var requestDate = currentDate;
      var requestType = currentMealType;
      candidateSlotRef.current = null;
      // At most one live candidate request owns the screen: the previous one
      // is aborted, not merely ignored when it lands.
      if (candidateAbortRef.current) candidateAbortRef.current.abort();
      var controller = newRequestController();
      candidateAbortRef.current = controller;
      var started = Date.now();
      guidedDiag('candidate:start', { slot: slot, id: requestId });
      setCandidateLoading(true);
      // Slot projections are request-specific. Clear the previous slot before
      // asking for the new one so it can never be selected into this slot.
      setMeals([]);
      setRemaining(null);
      setTarget(null);
      setCandidateTargetsReady(false);
      setCandidateError(null);
      // A POST: never re-sent automatically (explicit Retry only), but it has
      // the same finite boundary and is aborted when retired.
      guidedPlanningAPI
        .getMeals(currentDate, currentMealType, 12, userCookware, userMaxCookTime, null,
          { signal: controller.signal, timeout: GUIDED_REQUEST_TIMEOUT_MS })
        .then(function (res) {
          // Only the newest candidate request fills the step; a slower answer
          // for a slot or date the user has left is ignored.
          guidedDiag('candidate:settle', { slot: slot, id: requestId, ok: true, ms: Date.now() - started,
            stale: requestId !== candidateRequestRef.current });
          if (requestId !== candidateRequestRef.current) return;
          var data = res.data || {};
          // A response for another date / slot than the one asked for is never
          // made selectable here.
          if ((data.date && data.date !== requestDate) || (data.meal_type && data.meal_type !== requestType)) {
            setCandidateError('We could not load recommendations for this step. Retry to continue.');
            return;
          }
          setMeals(data.meals || []);
          setRemaining(data.remaining);
          setTarget(data.target);
          setCandidateTargetsReady(data.targets_ready === true);
          candidateSlotRef.current = slot;
        })
        .catch(function (err) {
          guidedDiag('candidate:settle', { slot: slot, id: requestId, ok: false, ms: Date.now() - started,
            aborted: isAbortError(err), code: err && err.code, stale: requestId !== candidateRequestRef.current });
          if (requestId !== candidateRequestRef.current || isAbortError(err)) return;
          console.error('[GuidedPlanning] Meals error:', err);
          setMeals([]);
          setRemaining(null);
          setTarget(null);
          setCandidateTargetsReady(false);
          setCandidateError('We could not load recommendations for this step. Retry to continue.');
        })
        .finally(function () {
          var current = requestId === candidateRequestRef.current;
          guidedDiag('candidate:finally', { slot: slot, id: requestId, clearsLoading: current });
          if (candidateAbortRef.current === controller) candidateAbortRef.current = null;
          if (current) setCandidateLoading(false);
        });
    },
    [currentDate, currentMealType, userCookware, userMaxCookTime],
  );

  // Simple refresh trigger - increment to force re-fetch
  var [refreshTrigger, setRefreshTrigger] = useState(0);

  // Function to force a refresh
  function forceRefresh() {
    setRefreshTrigger(function (prev) {
      return prev + 1;
    });
  }

  // Mirror the current day + meal type into the URL (replace, keeping the
  // flow's history state) so returns and refreshes resume this exact step.
  useEffect(
    function () {
      var next = new URLSearchParams(searchParams);
      if (currentDate) next.set('day', currentDate);
      else next.delete('day');
      if (currentMealType) next.set('type', currentMealType);
      else next.delete('type');
      // Consume the one-shot Overview-return marker: overviewPreserveRef was
      // already seeded from it on mount, so it must not linger in the URL and
      // permanently lock the page to the Overview.
      next.delete('overview');
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true, state: location.state });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currentDate, currentMealType],
  );

  // Fetch status when date changes or refresh is triggered
  useEffect(
    function () {
      if (currentDate) {
        fetchStatus();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currentDate, refreshTrigger],
  );

  // Retire the candidate request on screen: it is aborted, can never land,
  // never holds the spinner, and no card stays selectable.
  function retireCandidateRequest() {
    candidateRequestRef.current += 1;
    candidateSlotRef.current = null;
    if (candidateAbortRef.current) {
      candidateAbortRef.current.abort();
      candidateAbortRef.current = null;
    }
    setCandidateLoading(false);
  }

  // Retire the canonical load on screen (Back while it is still pending): it
  // is aborted and releases the spinner. The last known-good day for this date
  // stays on screen when there is one that still matches the saved day;
  // otherwise the recoverable reload state is shown -- never an endless wait.
  function retireCanonicalRequest() {
    canonicalRequestRef.current += 1;
    if (canonicalAbortRef.current) {
      canonicalAbortRef.current.abort();
      canonicalAbortRef.current = null;
    }
    guidedDiag('canonical:retired', { date: currentDate });
    setCanonicalLoading(false);
    var lastGood = !!status && statusDateRef.current === currentDate && !savedDayChangedRef.current;
    if (!lastGood) setCanonicalError('We could not reload the saved day. Retry before continuing.');
  }

  // Fetch meals when meal type changes (a Snack step only while the backend
  // still says another Snack is available; otherwise it shows the saved
  // Snacks only).
  useEffect(
    function () {
      if (currentMealType && currentDate && candidatesAllowed) {
        fetchMeals();
      } else {
        // No step on screen: a candidate answer still in flight belongs to a
        // step the user has left, so it must neither land nor hold the spinner.
        retireCandidateRequest();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currentMealType, currentDate, candidatesAllowed],
  );

  // Leaving the screen retires every live request it owns.
  useEffect(function () {
    return function () {
      canonicalRequestRef.current += 1;
      candidateRequestRef.current += 1;
      searchRequestRef.current += 1;
      [canonicalAbortRef, candidateAbortRef, searchAbortRef].forEach(function (ref) {
        if (ref.current) ref.current.abort();
        ref.current = null;
      });
    };
  }, []);

  // Expanded search. The instant local filter always runs first; only when it
  // leaves fewer than GUIDED_SEARCH_LOCAL_MIN useful matches (and the query has
  // at least 2 characters) does a debounced request ask the SAME Guided endpoint
  // for the slot's full eligible catalog. Any change of query, date, slot or
  // base recommendations retires the pending / in-flight request, so a stale
  // answer can never land on a newer query, date or slot.
  var localMatchCount = searchQuery.trim() ? filterMealsLocally(meals, searchQuery).length : meals.length;
  useEffect(
    function () {
      searchRequestRef.current += 1;
      setExpandedSearching(false);
      var q = searchQuery.trim();
      var slot = currentDate + '|' + currentMealType;
      if (!currentMealType || !candidatesAllowed || q.length < GUIDED_SEARCH_MIN_CHARS ||
          localMatchCount >= GUIDED_SEARCH_LOCAL_MIN) {
        setExpandedResults([]);
        return undefined;
      }
      // Wait until this slot's own recommendations are on screen.
      if (candidateLoading || candidateSlotRef.current !== slot) {
        setExpandedResults([]);
        return undefined;
      }
      var cacheKey = slot + '|' + q.toLowerCase();
      if (searchCacheRef.current[cacheKey]) {
        setExpandedResults(searchCacheRef.current[cacheKey]);
        return undefined;
      }
      setExpandedResults([]);
      var timer = setTimeout(function () {
        var requestId = ++searchRequestRef.current;
        guidedDiag('search:start', { slot: slot, id: requestId });
        setExpandedSearching(true);
        if (searchAbortRef.current) searchAbortRef.current.abort();
        var controller = newRequestController();
        searchAbortRef.current = controller;
        guidedPlanningAPI
          .getMeals(currentDate, currentMealType, GUIDED_SEARCH_MAX_RESULTS, userCookware, userMaxCookTime, q,
            { signal: controller.signal, timeout: GUIDED_REQUEST_TIMEOUT_MS })
          .then(function (res) {
            if (requestId !== searchRequestRef.current || candidateSlotRef.current !== slot) return;
            var found = (res && res.data && res.data.meals) || [];
            searchCacheRef.current[cacheKey] = found;
            setExpandedResults(found);
          })
          .catch(function () {
            if (requestId !== searchRequestRef.current) return;
            setExpandedResults([]);
          })
          .finally(function () {
            guidedDiag('search:finally', { slot: slot, id: requestId, current: requestId === searchRequestRef.current });
            if (requestId === searchRequestRef.current) setExpandedSearching(false);
          });
      }, GUIDED_SEARCH_DEBOUNCE_MS);
      return function () {
        clearTimeout(timer);
        searchRequestRef.current += 1;
        if (searchAbortRef.current) {
          searchAbortRef.current.abort();
          searchAbortRef.current = null;
        }
      };
    },
    // userCookware / userMaxCookTime are re-derived from the URL every render;
    // the search belongs to the query / date / slot / base list below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQuery, currentDate, currentMealType, meals, candidateLoading, candidatesAllowed, localMatchCount],
  );

  // Weekly completed/check state for the OTHER dates of a multi-day plan,
  // reconciled once per mount (a return from Swap / Browse / a meal's detail
  // remounts this screen) from each date's authoritative Guided status. The
  // date on screen is reconciled by its own canonical load; a canonical load
  // for any date supersedes this read for that date (completionVersionRef).
  useEffect(
    function () {
      if (allDates.length < 2) return undefined;
      var cancelled = false;
      allDates.forEach(function (d) {
        if (d === currentDateRef.current) return;
        var version = completionVersionRef.current[d] || 0;
        guidedPlanningAPI.getCompletionStatus(d).then(function (res) {
          if (cancelled || (completionVersionRef.current[d] || 0) !== version) return;
          var st = res && res.data;
          var complete = !!(st && st.planning_complete && savedDayStatusLabel(st) === PLANNING_STATUS.WITHIN);
          setCompletedDays(function (prev) {
            if (prev[d] === complete) return prev;
            var next = Object.assign({}, prev);
            next[d] = complete;
            return next;
          });
        }).catch(function () {
          // Unknown stays unknown: no check is claimed without a status.
        });
      });
      return function () { cancelled = true; };
    },
    // allDates is re-derived from datesParam every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datesParam],
  );

  function addMeal(meal) {
    // Only a card ranked for the step on screen is ever saved into it -- never
    // one left over from another slot or date while that step's cards load.
    if (candidateSlotRef.current !== currentDate + '|' + currentMealType) return;
    // Another Snack is added only while the backend's Snack rule offers one.
    if (!candidatesAllowed) return;
    // An identical Snack already pending in this slot is never "added" again:
    // the add path would collapse it into (and reset) the saved one.
    if (currentMealType === 'Snack' && pendingSnackIds()[meal.meal_id]) return;
    setAdding(meal.meal_id);

    mealPlanAPI
      .addMealPlan({
        date: currentDate,
        meal_type: currentMealType,
        meal_id: meal.meal_id,
        meal_name: meal.name,
        source: 'guided',
      })
      .then(function () {
        // Track meal add event
        trackMealAddToDay(currentMealType);
        savedDayChangedRef.current = true;

        toast.success(meal.name + ' added to ' + currentMealType);

        // If in single slot mode, navigate back to home
        if (isSingleSlotMode) {
          navigate('/home');
          return;
        }

        // The user moved to another date while this saved: that date's own
        // canonical load decides its step, not this date's status.
        if (currentDate !== currentDateRef.current) return;

        // Actively adding Snacks again: the refreshed status decides whether
        // another Snack is offered (stay in Snack) or the day is done.
        if (currentMealType === 'Snack') delete snackFinishedRef.current[currentDate];

        // Next step: the next unplanned Breakfast / Lunch / Dinner. Once none is
        // left, the refreshed status below decides -- a Snack step only when the
        // backend's shared Snack rule offers one, otherwise the day is done.
        var step = nextGuidedStep(currentMealType, status, { afterAdd: true });

        if (step.nextType) {
          setCurrentMealType(step.nextType);
        } else if (step.awaitStatus) {
          setCurrentMealType(null);
        } else {
          // Day complete - go to day overview; the forced refresh below reloads
          // canonical status which reconciles this date's completed state.
          setCurrentMealType(null);
        }

        // Force refresh to update status (useEffect will call fetchStatus)
        forceRefresh();
      })
      .catch(function (err) {
        console.error('[GuidedPlanning] Add error:', err);
        toast.error('Failed to add meal');
      })
      .finally(function () {
        setAdding(null);
      });
  }

  function removeMeal(planId) {
    setRemoving(planId);

    // A Remove initiated from Day Overview (no active meal step) must keep the
    // user on the Overview through the canonical refresh below.
    if (currentMealType === null) overviewPreserveRef.current = true;

    // Find the meal type for tracking
    const plan = plannedMeals.find((p) => p.plan_id === planId);
    const mealType = plan?.meal_type || currentMealType || 'Unknown';

    mealPlanAPI
      .deleteMealPlan(planId)
      .then(function () {
        // Track meal remove event
        trackMealRemoveFromDay(mealType);
        savedDayChangedRef.current = true;
        // P8b-B1: direct removal of an active planned recipe => weak negative.
        if (plan && plan.meal_id) recordTasteSignal(plan.meal_id, TASTE_ACTIONS.REMOVE);

        toast.success('Meal removed');
        // Force refresh to update the list (useEffect will call fetchStatus)
        forceRefresh();
      })
      .catch(function (err) {
        console.error('[GuidedPlanning] Remove error:', err);
        toast.error('Failed to remove meal');
      })
      .finally(function () {
        setRemoving(null);
      });
  }

  function swapMeal(plan) {
    // LEFTOVERS: a linked row never does the ordinary one-row delete+add Swap.
    // Deleting it cascades to its partner on the adjacent date, so that Swap
    // would silently remove the other day's meal and split the pair. Same
    // routing as Plan and Home.
    if (plan.leftover_group_id) {
      // Route from the backend's complete finalized-parent definition (status
      // as well as client-local date). A context failure never falls through
      // to destructive ordinary Swap.
      var sourceDate = currentDate;
      var linkedRequestId = ++linkedContextRequestRef.current;
      function ownsLinkedScreen() {
        return linkedRequestId === linkedContextRequestRef.current && sourceDate === currentDateRef.current;
      }
      mealPlanAPI.getMealPlanContext(plan.plan_id, getTodayString()).then(function (response) {
        if (!ownsLinkedScreen()) return;
        var context = response && response.data;
        if (!context || context.group_valid !== true || typeof context.parent_finalized !== 'boolean') {
          throw new Error('linked_context_unavailable');
        }
        if (plan.leftover_role === 'leftover' && context.parent_finalized) {
          // The normal single-meal Swap browser, finished by the atomic
          // child-only replacement endpoint; it returns to this exact step.
          navigate(
            buildUrl('/meals', {
              guided: 'true',
              date: currentDate,
              type: plan.meal_type,
              return: getCurrentReturnUrl(),
              swap: 'true',
              replacePlanId: plan.plan_id,
              linkedChild: '1',
              expectedMealId: plan.meal_id || '',
            }),
            { state: startFlowState() },
          );
          return;
        }
        if (context.parent_finalized) {
          toast.error('This cooked meal can no longer be replaced');
          return;
        }
        // A still-changeable pair -> pair-safe Replace Both (the dialog reads
        // the authoritative pair, including the partner on the other date).
        setPairSwapContext({
          groupId: plan.leftover_group_id,
          planId: plan.plan_id,
          date: currentDate,
          pairRows: plannedMeals
            .filter(function (m) {
              return m.leftover_group_id === plan.leftover_group_id;
            })
            .map(function (m) {
              return Object.assign({}, m, { date: currentDate });
            }),
        });
      }).catch(function () {
        if (!ownsLinkedScreen()) return;
        toast.error('Unable to verify the linked meal');
      });
      return;
    }
    // Navigate to meal browser to swap; it returns to this exact step.
    navigate(
      buildUrl('/meals', {
        guided: 'true',
        date: currentDate,
        type: plan.meal_type,
        return: getCurrentReturnUrl(),
        swap: 'true',
        replacePlanId: plan.plan_id,
        outgoingMealId: plan.meal_id,
      }),
      { state: startFlowState() },
    );
  }

  function skipMealType() {
    // Skipping does not change the saved day, so the current status's Snack
    // decision (the backend's shared Snack rule) still holds.
    var step = nextGuidedStep(currentMealType, status, { afterAdd: false });

    if (step.nextType) {
      setCurrentMealType(step.nextType);
    } else {
      setCurrentMealType(null);
    }
  }

  // ✓ Finish on the Snack step: back to the Day Overview, and this date's later
  // refreshes keep the Overview instead of re-entering the Snack step.
  function finishSnack() {
    snackFinishedRef.current[currentDate] = true;
    setMeals([]);
    setCurrentMealType(null);
    setSearchQuery('');
  }

  function addMoreMeals(mealType) {
    if (mealType === 'Snack' && !snackAvailable(status)) return;
    if (mealType === 'Snack') delete snackFinishedRef.current[currentDate];
    setMeals([]); // Clear meals immediately for UX
    setCurrentMealType(mealType);
    setSearchQuery(''); // Reset search when switching meal types
  }

  function goToNextDay() {
    if (currentDateIndex < allDates.length - 1) {
      setMeals([]); // Clear meals when switching days
      forceRefresh(); // Reset refs to allow new fetches
      setCurrentDateIndex(currentDateIndex + 1);
      setCurrentMealType(null);
      setSearchQuery(''); // Reset search when switching days
    } else {
      setShowAllComplete(true);
    }
  }

  function goToPrevDay() {
    if (currentDateIndex > 0) {
      setMeals([]); // Clear meals when switching days
      forceRefresh(); // Reset refs to allow new fetches
      setCurrentDateIndex(currentDateIndex - 1);
      setCurrentMealType(null);
      setSearchQuery(''); // Reset search when switching days
    }
  }

  // Leave the meal step for the Day Overview. Every request the step owned is
  // retired (aborted) and BOTH loading flags are released at once: a pending
  // canonical load can never keep the spinner over the Overview. The last
  // known-good day is shown when it still matches the saved day; otherwise the
  // recoverable reload state (Retry).
  function backToOverview() {
    retireCandidateRequest();
    if (canonicalLoading) retireCanonicalRequest();
    setMeals([]); // Clear meals when going back
    setCurrentMealType(null);
    setSearchQuery('');
  }

  function goBack() {
    if (currentMealType) {
      backToOverview();
    } else if (currentDateIndex > 0) {
      goToPrevDay();
    } else {
      // Leave Step-by-Step for exactly where it was opened from.
      finishFlow(navigate, location, returnTarget);
    }
  }

  // "View Full Plan": opened from Plan -> back to that exact Plan location;
  // opened from elsewhere (Home) -> Plan showing the days just planned.
  function goToPlan() {
    if (returnTarget.split('?')[0] === '/plan') finishFlow(navigate, location, returnTarget);
    else navigate(planPathForDates(allDates), { replace: true });
  }

  var plannedTypes = status && status.planned_meal_types ? status.planned_meal_types : [];

  // meal_ids already PENDING in the Snack slot (non-linked rows): the rows the
  // add path's same-slot dedupe would collapse a re-add into. The backend
  // already leaves them out of Snack candidates; this is the presentation
  // guard for any response / search state that still carries one.
  function pendingSnackIds() {
    return pendingSnackMealIds(plannedMeals);
  }

  // Group planned meals by type
  function getMealsByType(type) {
    return plannedMeals.filter(function (p) {
      return p.meal_type === type;
    });
  }

  // Render day progress indicators for multi-day
  function renderDayIndicators() {
    var indicators = [];
    for (var i = 0; i < allDates.length; i++) {
      (function (idx) {
        var dateStr = allDates[idx];
        var isCompleted = completedDays[dateStr];
        var isCurrent = idx === currentDateIndex;

        indicators.push(
          <button
            key={dateStr}
            onClick={function () {
              setMeals([]); // Clear meals when switching days
              setCurrentDateIndex(idx);
              setShowAllComplete(false);
              setCurrentMealType(null);
              setSearchQuery(''); // Reset search too
            }}
            className={
              'flex flex-col items-center px-3 py-2 rounded-lg transition-all ' +
              (isCurrent ? 'bg-accent text-white' : isCompleted ? 'bg-accent/20 text-accent' : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50')
            }
            data-testid={'day-indicator-' + dateStr}
          >
            <span className="text-xs font-medium">{formatShortDate(dateStr)}</span>
            {isCompleted && <Check size={12} className="mt-0.5" />}
          </button>,
        );
      })(i);
    }
    return indicators;
  }

  // Render meal type progress for current day - clickable to navigate
  function renderMealTypeProgress() {
    var items = [];
    for (var i = 0; i < mealSequence.length; i++) {
      (function (index) {
        var mealType = mealSequence[index];
        var isCompleted = plannedTypes.includes(mealType);
        var isCurrent = mealType === currentMealType;
        // Snack is enterable if a Snack is already saved/planned, or another
        // one is available (can_add_snacks) -- a user choice, independent of
        // whether Guided Planning would send them there. Frontend-only gate.
        var canEnter = mealType !== 'Snack' || plannedTypes.includes('Snack') || snackAvailable(status);

        items.push(
          <div key={mealType} className={'flex items-center gap-1.5 ' + (isCurrent ? 'text-accent' : isCompleted ? 'text-accent/60' : 'text-muted-foreground')}>
            <button
              onClick={function () {
                if (!canEnter) return;
                setMeals([]); // Clear meals when switching type
                setCurrentMealType(mealType);
                setSearchQuery(''); // Reset search too
              }}
              disabled={!canEnter}
              className={
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border transition-all hover:scale-110 ' +
                (isCompleted ? 'bg-accent border-accent text-white hover:bg-accent/80' : isCurrent ? 'border-accent bg-accent/10' : 'border-border/40 hover:border-accent/50 hover:bg-accent/5')
              }
              data-testid={'meal-nav-' + mealType.toLowerCase()}
              title={'Go to ' + mealType}
            >
              {isCompleted ? <Check size={12} /> : mealType.charAt(0)}
            </button>
            {index < mealSequence.length - 1 && <ChevronRight size={12} className="text-border/60" />}
          </div>,
        );
      })(i);
    }
    return items;
  }

  // Render day overview with planned meals (editable)
  function renderDayOverview() {
    var totals = status && status.committed_totals;
    var realTarget = status && status.targets_ready ? status.target : null;
    var dayLabel = savedDayStatusLabel(status);
    var acceptable = dayLabel === PLANNING_STATUS.WITHIN && status && status.planning_complete;
    // Day-status text color, the Home (TodaySummaryCard) convention: within cyan, under muted, over amber.
    var dayStatusColor = dayLabel === PLANNING_STATUS.UNDER ? 'text-muted-foreground'
      : (dayLabel === PLANNING_STATUS.OUT ? 'text-amber-600' : 'text-accent');
    // Same compact summary as Plan > Daily (PlanningHub renderDailyMacroSummary): a thin bar
    // colored by getMacroBarColor, then planned/goal and a short label.
    var macroItems = [
      { label: 'Cal', macro: 'calories' },
      { label: 'P', macro: 'protein' },
      { label: 'C', macro: 'carbs' },
      { label: 'F', macro: 'fat' },
    ];
    return (
      <div className="p-4 space-y-4">
        <div className="p-4 rounded-xl border border-border/30 bg-secondary/20" data-testid="macro-diagnostic">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-sm font-bold text-foreground" data-testid="nutrition-summary-label">
              Nutrition summary
            </span>
            {dayLabel ? (
              <span className={'text-sm font-medium ' + dayStatusColor} data-testid="saved-day-status">
                {dayLabel}
              </span>
            ) : null}
          </div>
          {totals ? (
            <div className="grid grid-cols-4 gap-2" data-testid="day-macro-summary">
              {macroItems.map(function (item) {
                var planned = Math.round(totals[item.macro]);
                var goal = realTarget ? realTarget[item.macro] : null;
                var pct = goal > 0 ? Math.round((planned / goal) * 100) : 0;
                return (
                  <div key={item.label} className="text-center" data-testid={'day-macro-' + item.macro}>
                    {/* No bar or goal without real targets: nothing is judged against invented values. */}
                    {realTarget ? (
                      <div className="h-1.5 bg-secondary rounded-full overflow-hidden mb-1">
                        <div
                          className={'h-full rounded-full transition-all ' + getMacroBarColor(pct, item.macro, status.goal)}
                          style={{ width: Math.min(pct, 100) + '%' }}
                          data-testid={'day-macro-bar-' + item.macro}
                        />
                      </div>
                    ) : null}
                    <p className="text-xs font-mono">
                      <span>{planned}</span>
                      {realTarget ? <span className="text-muted-foreground">/{goal}</span> : null}
                    </p>
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Saved nutrition totals are unavailable. Retry to review this day.</p>
          )}
          {status && status.targets_ready !== true ? (
            <p className="text-xs text-muted-foreground mt-3" data-testid="targets-not-ready">
              Set up your nutrition targets and goal to judge this day.
            </p>
          ) : null}
        </div>
        {/* Repair / navigation actions live ABOVE the meal-card list. On the
            Day Overview the repair actions are shown directly (no extra
            Review & Fix gate); a chosen action's result/alternatives panel
            temporarily replaces or accompanies them. */}
        {renderDayOverviewActions(acceptable)}
        {mealSequence.map(function (mealType) {
          var rows = getMealsByType(mealType);
          return (
            <div key={mealType} className="space-y-2">
              <div className="flex justify-between">
                <h3 className="font-medium text-sm">{mealType}</h3>
                {mealType !== 'Snack' || snackAvailable(status) ? (
                  <button onClick={function () { addMoreMeals(mealType); }} className="text-xs text-accent" data-testid={'overview-add-' + mealType.toLowerCase()}>
                    + Add
                  </button>
                ) : null}
              </div>
              {rows.length ? (
                rows.map(function (plan) {
                  return (
                    <PlannedMealCard
                      key={plan.plan_id}
                      plan={plan}
                      mealDetails={mealDetails[plan.meal_id]}
                      onSwap={swapMeal}
                      onRemove={removeMeal}
                      onViewMeal={handleViewPlannedMeal}
                      removing={removing}
                    />
                  );
                })
              ) : (
                <div className="p-3 border border-dashed rounded-lg text-xs text-muted-foreground">No {mealType.toLowerCase()} planned</div>
              )}
            </div>
          );
        })}
        <AnimatePresence>
          {showAcceptConfirm ? (
            <motion.div
              className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="accept-confirm"
            >
              <div className="bg-background rounded-xl p-5 max-w-sm">
                <h3 className="font-semibold">Keep this off-range day?</h3>
                <p className="text-sm text-muted-foreground my-3">You are choosing to keep the current saved day even though it is outside the recommended range.</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={function () {
                      setShowAcceptConfirm(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={function () {
                      setShowAcceptConfirm(false);
                      goToPlan();
                    }}
                  >
                    Keep day
                  </Button>
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }

  // Day Overview action area. On the Overview the repair actions are shown
  // directly (no extra "Review & Fix" gate). While a chosen action has a
  // focused sub-panel (swap alternatives), that panel takes over; a terminal
  // message (e.g. an Auto Fix explanation) is shown ABOVE the still-available
  // action group so repair actions remain reachable without an extra click.
  function renderDayOverviewActions(acceptable) {
    if (reviewResult && reviewResult.kind === 'swaps') {
      return renderReviewResult();
    }
    return (
      <>
        {reviewResult ? renderReviewResult() : null}
        {renderNavigationButtons(acceptable ? 'on-track' : 'needs-adjustment')}
      </>
    );
  }

  function renderReviewResult() {
    if (reviewResult.kind === 'swaps') {
      var suggestion = reviewResult;
      var group = suggestion.group;
      return (
        <div className="p-4 rounded-xl border space-y-3" data-testid="swap-suggestions-panel">
          <p className="font-medium">Replace {group.current_meal.name}</p>
          <p className="text-xs text-muted-foreground">
            {group.current_meal.meal_type} · {group.current_meal.calories} cal · {group.current_meal.protein}g P
          </p>
          {group.alternatives.map(function (alt) {
            return (
              <Button
                key={alt.meal_id}
                variant="outline"
                disabled={applyingFix}
                className="w-full justify-between"
                data-testid={'suggested-swap-' + alt.meal_id}
                onClick={function () {
                  chooseSuggestedSwap(suggestion, alt);
                }}
              >
                <span>{alt.name}</span>
                <span>{alt.calories} cal</span>
              </Button>
            );
          })}
          <Button
            variant="ghost"
            onClick={function () {
              setReviewResult(null);
            }}
          >
            Back
          </Button>
        </div>
      );
    }
    return (
      <div className={'p-4 rounded-xl border ' + (reviewResult.success ? 'bg-accent/10' : 'bg-secondary/30')} data-testid="review-terminal">
        <p className="font-medium">{reviewResult.title}</p>
        <p className="text-sm text-muted-foreground">{reviewResult.description}</p>
        <Button
          className="mt-3"
          variant="outline"
          onClick={function () {
            setReviewResult(null);
          }}
        >
          Dismiss
        </Button>
      </div>
    );
  }

  function renderNavigationButtons(statusType) {
    var acceptable = statusType === 'on-track';
    var action = isMultiDay && currentDateIndex < allDates.length - 1 ? goToNextDay : goToPlan;
    if (acceptable) {
      return (
        <div className="pt-4 space-y-3">
          <Button onClick={action} className="w-full h-12 rounded-full" data-testid="save-plan-btn">
            Save Day Plan
          </Button>
          {isMultiDay ? (
            <Button onClick={goToPlan} variant="outline" className="w-full h-10 rounded-full" data-testid="exit-btn">
              Save &amp; Exit
            </Button>
          ) : null}
        </div>
      );
    }
    // Off-range: a compact repair action group directly on the Day Overview.
    // The three actions flex and wrap gracefully on narrow screens instead of
    // stacking as full-width buttons.
    return (
      <div className="pt-4 space-y-3">
        <div className="flex flex-wrap gap-2" data-testid="repair-actions">
          <Button onClick={handleAutoFix} disabled={applyingFix} className="flex-1 min-w-[30%] h-11 rounded-full" data-testid="auto-fix-btn">
            {applyingFix ? 'Fixing…' : 'Auto-Fix'}
          </Button>
          <Button onClick={handleSuggestSwaps} disabled={applyingFix} variant="outline" className="flex-1 min-w-[30%] h-11 rounded-full" data-testid="suggest-swaps-btn">
            Suggest Swaps
          </Button>
          <Button onClick={handleAcceptAsIs} disabled={applyingFix || !status || status.targets_ready !== true} variant="outline" className="flex-1 min-w-[30%] h-11 rounded-full" data-testid="accept-as-is-btn">
            Accept As Is
          </Button>
        </div>
        {status && status.targets_ready !== true ? (
          <p className="text-xs text-muted-foreground">Nutrition setup is not ready, so this day cannot be accepted as within range.</p>
        ) : null}
        {isMultiDay ? (
          <Button onClick={goToPlan} variant="outline" className="w-full h-10 rounded-full" data-testid="exit-btn">
            Save &amp; Exit
          </Button>
        ) : null}
      </div>
    );
  }

  var AUTO_FIX_COPY = {
    already_good: ['Day already in range', 'No repair was needed.', true],
    fixed: ['Day fixed', 'The repair was saved and verified.', true],
    constrained: ['No valid fix found', 'The current constraints and catalog could not produce a valid fix.', false],
    setup_unavailable: ['Nutrition setup unavailable', 'Targets and an explicit goal are required before this day can be fixed.', false],
    incomplete: ['Could not complete the search', 'No complete valid repair was produced.', false],
    write_failure: ['Could not safely save the fix', 'The saved result could not be verified.', false],
  };
  function handleAutoFix() {
    if (applyingFix) return;
    setApplyingFix(true);
    setReviewResult(null);
    planningAPI
      .autoFix(currentDate)
      .then(function (res) {
        var data = res.data || {};
        // Internal diagnostics keep the backend's machine reason; the screen
        // keeps its one simple message per terminal status.
        reportAutoFixTerminal(data);
        var copy = AUTO_FIX_COPY[data.status] || ['Unexpected Auto Fix result', 'The server returned an unknown result.', false];
        if (data.status === 'fixed') savedDayChangedRef.current = true;
        // Auto-Fix is a Day Overview action: its canonical reload keeps the
        // Overview (never next_meal_type, a guided or an optional Snack).
        if (copy[2]) overviewPreserveRef.current = true;
        var reload =
          copy[2]
            ? fetchStatus().then(function (reloadResult) {
                // A superseded reload applied nothing: neither success nor failure.
                if (reloadResult && reloadResult.stale) return { show: false, statusData: null };
                if (!reloadResult || !reloadResult.complete) throw new Error('canonical_reload_failed');
                return { show: true, statusData: reloadResult.statusData };
              })
            : Promise.resolve({ show: true, statusData: null });
        return reload.then(function (r) {
          // The result belongs to the saved day it ran for: never shown on a
          // date the user has moved to, nor over a newer reload of this date.
          if (!r.show || currentDate !== currentDateRef.current) return;
          var refreshed = r.statusData;
          // Inspect the REFRESHED authoritative status (never stale pre-write
          // React state): once the fixed day is complete + Within Range, drop
          // the terminal panel and return to the normal Day Overview so
          // Save Day Plan is visible. Otherwise keep the explanation while the
          // repair actions remain available on the Overview.
          if (
            copy[2] &&
            refreshed &&
            refreshed.planning_complete === true &&
            savedDayStatusLabel(refreshed) === PLANNING_STATUS.WITHIN
          ) {
            setReviewResult(null);
            return;
          }
          setReviewResult({
            kind: 'auto',
            title: copy[0],
            description: copy[1],
            success: copy[2],
          });
        });
      })
      .catch(function (err) {
        reportAutoFixTerminal({ status: 'request_failed', reason: (err && err.code) || null });
        toast.error('Auto Fix could not be completed');
      })
      .finally(function () {
        setApplyingFix(false);
      });
  }
  function handleSuggestSwaps() {
    if (applyingFix) return;
    // The date these recommendations are generated for. They are kept with it
    // and are only ever offered on, or written into, that date.
    var sourceDate = currentDate;
    setApplyingFix(true);
    setReviewResult(null);
    planningAPI
      .suggestSwaps(sourceDate)
      .then(function (res) {
        // Recommendations belong to the saved day they were asked for; they are
        // never offered on (or written into) a date the user has moved to.
        if (sourceDate !== currentDateRef.current) return;
        var data = res.data || {};
        var groups = data.swap_suggestions || [];
        if (groups.length > 1) {
          setReviewResult({
            kind: 'error',
            title: 'Unexpected response',
            description: 'More than one swap target was returned.',
            success: false,
          });
        } else if (data.status === 'recommendations_available' && groups.length === 1) {
          setReviewResult({ kind: 'swaps', group: groups[0], sourceDate: sourceDate });
        } else if (data.status === 'not_ready') {
          setReviewResult({
            kind: 'empty',
            title: 'Nutrition setup unavailable',
            description: 'Targets and an explicit goal are required.',
            success: false,
          });
        } else {
          setReviewResult({
            kind: 'empty',
            title: 'No valid swap recommendation',
            description: 'No recommendation is available under the current constraints.',
            success: false,
          });
        }
      })
      .catch(function () {
        toast.error('Failed to find swap suggestions');
      })
      .finally(function () {
        setApplyingFix(false);
      });
  }
  // Write-time date guard: a Suggest Swaps recommendation may only change the
  // date it was generated for (its sourceDate), and only while that date is
  // the one on screen -- never whatever date a stale handler would infer from
  // the render it came from. A mismatch performs no delete and no add; the
  // stale result is dropped and the user can ask again for the day on screen.
  function chooseSuggestedSwap(suggestion, alternative) {
    if (applyingFix) return;
    var sourceDate = suggestion.sourceDate;
    if (!sourceDate || sourceDate !== currentDateRef.current) {
      setReviewResult(function (shown) {
        return shown === suggestion ? null : shown;
      });
      toast.info('Those swap suggestions were for another day. Ask for suggestions again.');
      return;
    }
    var current = suggestion.group.current_meal;
    setApplyingFix(true);
    // ONE atomic, idempotent replacement of exactly this plan_id (never a
    // delete followed by a generic add). The expected_meal_id stale guard
    // rejects the write if the target row changed since the suggestion.
    var operationId = 'sugswap_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    planningAPI
      .applySuggestion(sourceDate, 'swap', current.meal_type, alternative.meal_id, alternative.name,
        current.plan_id, operationId, current.meal_id)
      .then(function () {
        recordSwapTasteSignals(current.meal_id, alternative.meal_id);
        setReviewResult(null);
        savedDayChangedRef.current = true;
        // Suggest Swaps is a Day Overview action: its canonical reload keeps the
        // Overview (never next_meal_type, a guided or an optional Snack).
        overviewPreserveRef.current = true;
        return fetchStatus();
      })
      .then(function (reloadResult) {
        // Another date, or a newer reload of this one, owns the screen and
        // shows the saved day itself.
        if (reloadResult && reloadResult.stale) return;
        if (!reloadResult || !reloadResult.complete) throw new Error('canonical_reload_failed');
        toast.success('Swapped to ' + alternative.name);
        // Inspect the REFRESHED authoritative status (not stale pre-write React
        // state). If the day is now complete + Within Range, the cleared
        // reviewResult (above) returns the user to the normal Day Overview with
        // Save Day Plan visible. If it is still off-range, the Overview's
        // compact repair action group remains available for another pass.
        var refreshed = reloadResult.statusData;
        if (
          refreshed &&
          refreshed.planning_complete === true &&
          savedDayStatusLabel(refreshed) === PLANNING_STATUS.WITHIN
        ) {
          setReviewResult(null);
        }
      })
      .catch(function (err) {
        var detail = err && err.response && err.response.data && err.response.data.detail;
        if (err && err.message === 'canonical_reload_failed') {
          setReviewResult({ kind: 'error', title: 'Swap saved; reload needed', description: 'The swap was saved, but the canonical day could not be reloaded. Retry the day before continuing.', success: false });
          toast.error('Swap saved, but the day could not be reloaded');
        } else if (detail && (detail.error === 'stale_target' || detail.error === 'not_swappable')) {
          setReviewResult(null);
          toast.error('That meal changed since the suggestion. Ask for suggestions again.');
          overviewPreserveRef.current = true;
          fetchStatus();
        } else toast.error('Failed to swap meal');
      })
      .finally(function () {
        setApplyingFix(false);
      });
  }
  // Which top-level branch the screen renders (diagnostic attribute + log).
  var renderBranch = loading ? 'loading' : canonicalError ? 'canonical-error' : showAllComplete ? 'all-complete'
    : currentMealType ? 'selection' : 'overview';
  useEffect(
    function () {
      guidedDiag('render:branch', { branch: renderBranch, date: currentDate, type: currentMealType,
        canonicalLoading: canonicalLoading, candidateLoading: candidateLoading });
    },
    [renderBranch, currentDate, currentMealType, canonicalLoading, candidateLoading],
  );

  function handleAcceptAsIs() {
    var label = savedDayStatusLabel(status);
    if (status && status.targets_ready === true && status.planning_complete && label === PLANNING_STATUS.WITHIN) goToPlan();
    else if (status && status.targets_ready === true) setShowAcceptConfirm(true);
  }

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="guided-root"
      data-canonical-loading={canonicalLoading ? 'true' : 'false'}
      data-candidate-loading={candidateLoading ? 'true' : 'false'}
      data-current-date={currentDate || ''}
      data-current-type={currentMealType || ''}
      data-render-branch={renderBranch}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        <div className="px-4 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <Button onClick={goBack} variant="ghost" size="icon" className="rounded-full h-9 w-9" data-testid="back-btn">
              <ArrowLeft size={18} />
            </Button>
            <div className="flex-1">
              <h1 className="font-heading font-bold text-lg" data-testid="guided-title">{isSingleSlotMode ? 'Add ' + fillSlotParam + ' Meal' : currentMealType === null ? 'Plan Your Day' : isMultiDay ? 'Plan Your Week' : 'Plan Your Day'}</h1>
              <p className="text-xs text-muted-foreground" data-testid="guided-subtitle">
                {isSingleSlotMode ? 'Select a meal to add' : currentMealType === null ? formatFullDateWithOrdinal(currentDate) : isMultiDay ? allDates.length + ' days • Day ' + (currentDateIndex + 1) + ' of ' + allDates.length : formatDate(currentDate)}
              </p>
            </div>
            {currentMealType && !isSingleSlotMode && (
              currentMealType === 'Snack' ? (
                <Button
                  onClick={finishSnack}
                  variant="ghost"
                  size="sm"
                  className="text-accent"
                  data-testid="finish-snack-btn"
                >
                  <Check size={14} className="mr-1" />
                  Finish
                </Button>
              ) : (
                <Button onClick={skipMealType} variant="ghost" size="sm" className="text-muted-foreground" data-testid="skip-btn">
                  Skip
                  <ArrowRight size={14} className="ml-1" />
                </Button>
              )
            )}
          </div>

          {/* Day indicators for multi-day planning */}
          {isMultiDay && !showAllComplete && (
            <div className="flex gap-1 overflow-x-auto pb-2 mb-2" data-testid="day-indicators">
              {renderDayIndicators()}
            </div>
          )}

          {/* Meal type progress for current day */}
          {!showAllComplete && (
            <div className="flex items-center gap-1" data-testid="meal-progress">
              {renderMealTypeProgress()}
            </div>
          )}
        </div>
      </div>

      {/* Remaining macros bar - only when selecting meals */}
      {remaining && currentMealType && !showAllComplete && (
        <RemainingMacrosHeader remaining={remaining} target={target}
          dayWithinRange={savedDayStatusLabel(status) === PLANNING_STATUS.WITHIN} />
      )}

        <AnimatePresence mode="wait">
        {loading ? (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex justify-center py-12" data-testid="loading-spinner">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
          </motion.div>
        ) : canonicalError ? (
          <motion.div key="canonical-error" className="p-6 text-center" data-testid="canonical-error">
            <AlertTriangle className="mx-auto text-amber-500 mb-3" />
            <p className="text-sm text-muted-foreground mb-4">{canonicalError}</p>
            <Button onClick={fetchStatus} variant="outline" data-testid="canonical-retry-btn">Retry</Button>
          </motion.div>
        ) : showAllComplete ? (
          <motion.div key="all-complete" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="p-6 text-center" data-testid="all-complete-screen">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
              <Check size={40} className="text-accent" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-2">Week Planned!</h2>
            <p className="text-muted-foreground mb-6">You've planned all {allDates.length} days</p>

            {/* Summary of planned days */}
            <div className="bg-card rounded-xl p-4 mb-6 border border-border/40">
              <p className="text-sm text-muted-foreground mb-3">Planned days:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {allDates.map(function (dateStr) {
                  return (
                    <span key={dateStr} className="px-3 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-medium">
                      {formatShortDate(dateStr)}
                    </span>
                  );
                })}
              </div>
            </div>

            <Button onClick={goToPlan} className="w-full h-12 rounded-full bg-accent text-white font-semibold" data-testid="view-plan-btn">
              View Full Plan
            </Button>
          </motion.div>
        ) : currentMealType ? (
          <motion.div
            key={currentDate + '-' + currentMealType}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="p-4"
            data-testid="meal-selection-screen"
          >
            <div className="mb-4">
              <h2 className="font-heading font-bold text-xl" data-testid="current-meal-type">
                Choose {currentMealType}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isMultiDay && <span className="text-accent">{formatShortDate(currentDate)} • </span>}
                {expandedSearching ? (
                  <span data-testid="searching-all-meals">Searching all meals</span>
                ) : (
                  meals.length + ' best matches'
                )}
              </p>
            </div>

            {/* Saved rows for this slot render first, above the recommendations */}
            {(function () {
              var savedRows = getMealsByType(currentMealType);
              if (!savedRows.length) return null;
              return (
                <div className="mb-4 space-y-2" data-testid="selected-meals-section">
                  <h3 className="font-medium text-sm">Selected Meals</h3>
                  {savedRows.map(function (plan) {
                    return (
                      <PlannedMealCard
                        key={plan.plan_id}
                        plan={plan}
                        mealDetails={mealDetails[plan.meal_id]}
                        onSwap={swapMeal}
                        onRemove={removeMeal}
                        onViewMeal={handleViewPlannedMeal}
                        removing={removing}
                      />
                    );
                  })}
                </div>
              );
            })()}

            {!candidatesAllowed ? (
              <div className="text-center py-6 text-muted-foreground" data-testid="snack-add-unavailable">
                <p className="text-sm">No other Snack fits your day right now.</p>
                <Button onClick={finishSnack} variant="outline" className="mt-4" data-testid="finish-snack-saved-btn">
                  <Check size={14} className="mr-1" />
                  Finish
                </Button>
              </div>
            ) : (
            <>
            {/* Search Bar */}
            <div className="relative mb-4">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={function (e) {
                  setSearchQuery(e.target.value);
                }}
                placeholder="Search meals..."
                className="w-full h-11 pl-10 pr-10 rounded-xl border border-border/40 bg-secondary/30 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                data-testid="meal-search-input"
              />
              {searchQuery && (
                <button
                  onClick={function () {
                    setSearchQuery('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              )}
            </div>

                <div className="space-y-3" data-testid="meal-cards-container">
                  {candidateError ? (
                    <div className="text-center py-8" data-testid="candidate-error">
                      <p className="text-sm text-muted-foreground mb-3">{candidateError}</p>
                      <Button onClick={fetchMeals} variant="outline" data-testid="candidate-retry-btn">Retry</Button>
                    </div>
                  ) : (
                  (function () {
                // Filter meals by search query: instant local matches first,
                // then expanded full-catalog results (deduplicated by meal_id).
                var filteredMeals = meals;
                if (searchQuery.trim()) {
                  filteredMeals = filterMealsLocally(meals, searchQuery);
                  var shownIds = {};
                  filteredMeals.forEach(function (m) { shownIds[m.meal_id] = true; });
                  filteredMeals = filteredMeals.concat(expandedResults.filter(function (m) {
                    if (shownIds[m.meal_id]) return false;
                    shownIds[m.meal_id] = true;
                    return true;
                  }));
                }

                if (filteredMeals.length > 0) {
                  var savedSnackIds = currentMealType === 'Snack' ? pendingSnackIds() : {};
                  return filteredMeals.map(function (meal, index) {
                    return (
                      <MealSelectionCard
                        key={meal.meal_id}
                        meal={meal}
                        mealType={currentMealType}
                        index={index}
                        adding={adding}
                        onAddMeal={addMeal}
                        onViewMeal={handleViewSelectionMeal}
                        targetsReady={candidateTargetsReady}
                        alreadyAdded={!!savedSnackIds[meal.meal_id]}
                      />
                    );
                  });
                } else if (searchQuery.trim() && expandedSearching) {
                  return null;
                } else if (searchQuery.trim()) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No meals found for "{searchQuery}"</p>
                      <Button
                        onClick={function () {
                          setSearchQuery('');
                        }}
                        variant="outline"
                        className="mt-4"
                      >
                        Clear Search
                      </Button>
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No meals available for this category.</p>
                      {currentMealType === 'Snack' ? (
                        <Button onClick={finishSnack} variant="outline" className="mt-4" data-testid="finish-snack-empty-btn">
                          <Check size={14} className="mr-1" />
                          Finish
                        </Button>
                      ) : (
                        <Button onClick={skipMealType} variant="outline" className="mt-4">
                          Skip {currentMealType}
                        </Button>
                      )}
                    </div>
                  );
                }
                  })()
                  )}
                </div>

            </>
            )}

            {/* Browse more option */}
            {meals.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border/20">
                <Button
                  onClick={function () {
                    navigate(
                      buildUrl('/meals', {
                        guided: 'true',
                        date: currentDate,
                        type: currentMealType,
                        return: getCurrentReturnUrl(),
                        stepBrowse: '1',
                      }),
                      { state: startFlowState() },
                    );
                  }}
                  variant="outline"
                  className="w-full h-10 rounded-full text-sm"
                  data-testid="browse-more-btn"
                >
                  Browse All {currentMealType} Options
                </Button>
              </div>
            )}

            {/* Back to day overview */}
            <div className="mt-4">
              <Button
                onClick={backToOverview}
                variant="ghost"
                className="w-full h-10 text-sm text-muted-foreground"
                data-testid="back-to-overview-btn"
              >
                Back to Day Overview
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div key={currentDate + '-overview'} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {renderDayOverview()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* LEFTOVERS: pair-safe Replace Both, shared with Plan and Home. Cancel
          writes nothing; a replacement reloads the canonical saved day. */}
      <PairSwapDialog
        context={pairSwapContext}
        onClose={function () {
          setPairSwapContext(null);
        }}
        onSwapped={function () {
          savedDayChangedRef.current = true;
          forceRefresh();
        }}
      />
    </div>
  );
}

export default GuidedPlanning;
